import type { StatusDefinition, ShipmentLike, ResolvedShipmentStatus } from "./types";
import { FALLBACK_STATUS } from "./types";

// Persisted loadStatus values that need classification-based branching.
const RTQ = "Ready to Quote";
const RTB = "Ready to Book";

// Status codes the resolver can branch into for display-only variants.
const RQS = "RQS";
const RQI = "RQI";
const RBS = "RBS";
const RBI = "RBI";

export const CLASSIFICATION_IN_SCOPE = "In-Scope";
export const CLASSIFICATION_INSUFFICIENT = "Insufficient Information";

// Match by statusCode first (deterministic, code is unique), then by display
// `status` text (which may have duplicates across codes).
export function findStatus(
  statuses: StatusDefinition[],
  identifier: string
): StatusDefinition | undefined {
  return (
    statuses.find((s) => s.statusCode === identifier) ??
    statuses.find((s) => s.status === identifier)
  );
}

function resolveStatusCode(shipment: ShipmentLike): string {
  const raw = shipment.loadStatus ?? RTQ;

  if (raw === RTQ) {
    if (shipment.classification === CLASSIFICATION_IN_SCOPE) return RQS;
    if (shipment.classification === CLASSIFICATION_INSUFFICIENT) return RQI;
    return RTQ;
  }

  if (raw === RTB) {
    if (shipment.bolClassification === CLASSIFICATION_IN_SCOPE) return RBS;
    if (shipment.bolClassification === CLASSIFICATION_INSUFFICIENT) return RBI;
    return RTB;
  }

  return raw;
}

function makeFallback(identifier: string): StatusDefinition {
  return { ...FALLBACK_STATUS, status: identifier, statusCode: identifier };
}

export function resolveShipmentStatus(
  shipment: ShipmentLike,
  statuses: StatusDefinition[]
): ResolvedShipmentStatus {
  const persistedStatus = shipment.loadStatus ?? null;
  const resolvedStatusCode = resolveStatusCode(shipment);
  const definition = findStatus(statuses, resolvedStatusCode) ?? makeFallback(resolvedStatusCode);

  return {
    ...definition,
    persistedStatus,
    resolvedStatusCode,
  };
}

/**
 * Resolve a single status-history row for display.
 * History may store a unique `statusCode` (RQS, RQI, …) or legacy persisted labels
 * ("Ready to Quote" / "Ready to Book"). The latter share the same display `status`
 * string across variants, so we must not use `findStatus(..., "Ready to Quote")` —
 * that returns whichever definition appears first in the collection (often RQI).
 */
export function resolveStatusHistoryEntry(
  entryStatus: string,
  ctx: { classification?: string | null; bolClassification?: string | null },
  statuses: StatusDefinition[]
): ResolvedShipmentStatus {
  const id = entryStatus.trim();

  if (!id) {
    return resolveShipmentStatus({ loadStatus: null, ...ctx }, statuses);
  }

  const byCode = statuses.find((s) => s.statusCode === id);

  if (byCode) {
    return {
      ...byCode,
      persistedStatus: id,
      resolvedStatusCode: id,
    };
  }

  if (id === RTQ || id === RTB) {
    return resolveShipmentStatus({ loadStatus: id, ...ctx }, statuses);
  }

  return resolveShipmentStatus({ loadStatus: id, ...ctx }, statuses);
}
